import { NextSeo } from 'next-seo'
import { getPosts, PostsOrPages } from '@src/lib/ghost'
import { formatDate, wordsOrDate } from '@src/lib/utils'
import SiteLayout from '@src/ui/components/site-layout'
import type { GetStaticProps } from 'next'
import Link from 'next/link'

type BlogProps = {
  posts: PostsOrPages
}
const Blog = (props: BlogProps) => {
  return (
    <SiteLayout>
      <NextSeo
        title='Blog'
        description='Articles and tutorials about building and deploying server side Swift from the Swift Cloud team.'
        canonical='https://swift.cloud/blog'
      />
      <div className='sm:px-6 lg:pt-24 lg:pb-28 lg:px-8 relative px-4 pt-8 pb-20 bg-white'>
        <div className='max-w-7xl relative mx-auto'>
          <div className='text-center'>
            <h2 className='sm:text-4xl text-slate-900 text-3xl font-bold tracking-tight'>
              Swift Cloud Blog
            </h2>
            <p className='text-slate-600 max-w-2xl mx-auto mt-2 text-xl'>
              Articles and tutorials from the Swift Cloud team.
            </p>
          </div>
          <div className='lg:grid-cols-3 lg:max-w-none grid max-w-lg gap-5 mx-auto mt-12'>
            {props.posts.map((post) => (
              <div key={post.title} className='flex flex-col overflow-hidden rounded-lg shadow-lg'>
                <div className='flex flex-col justify-between flex-1 p-6 bg-white'>
                  <div className='flex-1'>
                    <p className='text-sm font-medium text-orange-600'>
                      {post.primary_tag?.name}
                    </p>
                    <Link href={`/blog/${post.slug}`} legacyBehavior>
                      <a className='block mt-2'>
                        <p className='text-xl font-semibold text-gray-900'>{post.title}</p>
                        <p className='text-slate-600 mt-3 text-base'>{post.excerpt}</p>
                      </a>
                    </Link>
                  </div>
                  <div className='flex items-center mt-6'>
                    <div className='flex-shrink-0'>
                      <span className='sr-only'>{post.primary_author?.name}</span>
                      {post.primary_author?.profile_image ? (
                        <img className='w-10 h-10 rounded-full' src={post.primary_author?.profile_image} alt={post.primary_author?.name} />
                      ) : null}
                    </div>
                    <div className='ml-3'>
                      <p className='text-sm font-medium text-gray-900'>
                        {post.primary_author?.name}
                      </p>
                      <div className='text-slate-600 flex space-x-1 text-sm'>
                        <time dateTime={formatDate(post.published_at as string)}>{wordsOrDate(post.published_at as string)}</time>
                        <span aria-hidden='true'>&middot;</span>
                        <span>{post.reading_time} minute read</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </SiteLayout>
  )
}

export const getStaticProps: GetStaticProps = async () => {
  const posts = await getPosts()
  return {
    props: { posts },
    revalidate: 60
  }
}

export default Blog
